import isEmpty from 'lodash/isEmpty';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import classNames from 'classnames';
import featureFlags from 'common/helpers/featureFlags';
import ConditionalMultiSelectSearchWithLabel from '../../../common/components/ConditionalMultiSelectSearchWithLabel/ConditionalMultiSelectSearchWithLabel';
import { fetchConnectedContentPreference } from '../../../common/redux/userSettings/userSettingsSlice';
import ConnectedContentContext from '../Context/ConnectedContentContext';
import useConnectedNarratives from '../EditConnectedContent/useConnectedNarratives';
import { contentIsLinked } from '../narrativesUtilities';
import SelectedConnectedContentItem from './SelectedConnectedContentItem/SelectedConnectedContentItem';
import './EditConnectedContentNew.scss';
import '../EditNarrativeCard/ConnectContentModal.scss';

const EditConnectedContentNew = ({
  lastItem,
  projectId,
  parentContentId,
  setCurrentlyVisibleConnections,
  allConnections,
  connectedContentType,
  setConnectedContentChanged,
  currentAnalysis,
  error,
  isCommentingAllowedOnThisItem,
  pageContainerName,
  setConnectedAnalyses,
}) => {
  const [search, setSearch] = useState('');
  const { t } = useTranslation();
  const useParentContentId = parentContentId;
  const { previouslyConnectedNarratives } = useConnectedNarratives(
    useParentContentId,
    allConnections,
    search
  );

  const [assignedConnectedContent, setAssignedConnectedContent] = useState([]);

  useEffect(() => {
    if (
      connectedContentType === 'analyses' &&
      allConnections &&
      allConnections.analyses.length
    ) {
      allConnections.analyses.filter((byContent) => {
        if (contentIsLinked(byContent.links, useParentContentId)) {
          setAssignedConnectedContent((prevState) => {
            return [
              ...prevState.filter(
                (item) => item.contentThreadId !== byContent.contentThreadId
              ),
              byContent,
            ];
          });
        }
        return null;
      });
    } else if (
      connectedContentType === 'hypotheses' &&
      allConnections &&
      allConnections.hypotheses.length
    ) {
      allConnections.hypotheses.filter((byContent) => {
        if (contentIsLinked(byContent.links, useParentContentId)) {
          setAssignedConnectedContent([byContent]);
        }
        return null;
      });
    }
  }, [
    connectedContentType,
    previouslyConnectedNarratives,
    allConnections,
    useParentContentId,
  ]);

  const isLoadingConnections = useMemo(() => {
    if (!allConnections) {
      return true;
    }
    return false;
  }, [allConnections]);

  const { url } = useRouteMatch();

  const dispatch = useDispatch();
  const { previousConnections, setPreviousConnections, setLastSelectedKFA } =
    useContext(ConnectedContentContext);

  const contentTypes = useMemo(() => {
    if (url.includes('key-focus-areas')) {
      return ['analyses'];
    }
    return ['analyses', 'hypotheses'];
  }, [url]);

  useEffect(() => {
    if (lastItem) {
      const contentType = contentTypes.length > 1 ? 'analyses' : 'hypotheses';
      const featureName =
        contentType === 'analyses'
          ? 'Analysis_Link_Warning'
          : 'KFA_Link_Warning';
      dispatch(
        fetchConnectedContentPreference(projectId, contentType, featureName)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyConnectedContent = useMemo(() => {
    return contentTypes.reduce((accum, curr) => {
      accum[curr] = [];
      return accum;
    }, {});
  }, [contentTypes]);

  const filterByIsConnectedToParent = useCallback(
    (content) => {
      return content.links.some(
        (link) => link.id === parseInt(useParentContentId, 10) && link.linkId
      );
    },
    [useParentContentId]
  );

  const updatePreviousConnections = useCallback(
    (prevConnections) => {
      setPreviousConnections(prevConnections);
    },
    [setPreviousConnections]
  );

  const previousConnectedContent = useMemo(() => {
    if (!allConnections || !Object.keys(allConnections).length) {
      return emptyConnectedContent;
    }
    const prevConnections = contentTypes.reduce((accum, curr) => {
      accum[curr] = allConnections[curr].filter(filterByIsConnectedToParent);
      return accum;
    }, {});

    return prevConnections;
  }, [
    allConnections,
    contentTypes,
    filterByIsConnectedToParent,
    emptyConnectedContent,
  ]);

  useEffect(() => {
    if (!isEmpty(previousConnectedContent)) {
      updatePreviousConnections(previousConnectedContent);
    }
  }, [
    previousConnectedContent,
    previousConnections,
    updatePreviousConnections,
    assignedConnectedContent,
  ]);

  const linkNarrative = useCallback(
    (newNarrative, type) => {
      const replaceAtIndex = allConnections[type].findIndex(
        (n) => n.contentThreadId === newNarrative.contentThreadId
      );
      allConnections[type][replaceAtIndex] = newNarrative;

      if (setConnectedContentChanged) {
        setConnectedContentChanged();
      }

      setCurrentlyVisibleConnections(allConnections);
    },
    [allConnections, setCurrentlyVisibleConnections, setConnectedContentChanged]
  );

  const filterBySearch = useCallback(
    (content) => {
      const testString = (testSearch, string) =>
        (string || '').toLowerCase().includes(testSearch.toLowerCase());
      return (
        testString(search, content.title) ||
        testString(search, content.body) ||
        testString(search, content.conclusion)
      );
    },
    [search]
  );

  const filteredAvailableConnectedContent = useMemo(() => {
    if (allConnections && Object.keys(allConnections)) {
      const allContent = contentTypes.reduce((accum, curr) => {
        accum[curr] = allConnections[curr].filter(filterBySearch);

        return accum;
      }, {});

      const allWithoutCurrentContent = contentTypes.reduce((accum, curr) => {
        accum[curr] = allContent[curr].filter((phase) => {
          return phase.contentId !== currentAnalysis.contentId;
        });
        return accum;
      }, {});

      return connectedContentType === 'analyses'
        ? allWithoutCurrentContent.analyses
        : allWithoutCurrentContent.hypotheses;
    }

    return [];
  }, [
    contentTypes,
    allConnections,
    filterBySearch,
    connectedContentType,
    currentAnalysis,
  ]);

  const searchField = {
    name: 'connected-content-search',
    value: search,
    onChange: (e) => setSearch(e.target.value),
  };

  const handleSetSearch = () => {
    setSearch('');
  };

  const submitConnectedContent = useCallback(
    (values) => {
      const contentObjects = new Array(values.length);

      allConnections.analyses.forEach((analysis) => {
        if (
          values.includes(analysis.title) === true ||
          contentIsLinked(analysis.links, useParentContentId)
        ) {
          // make sure that connected analyses are displayed in the order they were selected
          contentObjects[values.indexOf(analysis.title)] = analysis;
        }
      });

      const found = contentObjects.filter(
        (v, i, a) =>
          a.findIndex((x) => x.contentThreadId === v.contentThreadId) === i
      );

      setAssignedConnectedContent(found);
    },
    [allConnections, useParentContentId]
  );

  const submitAnalyses = (values) => {
    submitConnectedContent(values);
  };

  const submitKeyFocusArea = useCallback(
    (keyFocusTitle) => {
      const kfaObject = filteredAvailableConnectedContent.find(
        (o) => o.title === keyFocusTitle[0]
      );

      allConnections.hypotheses.map((byNarrative) => {
        if (byNarrative.contentId !== kfaObject.contentId) {
          const existingLink = byNarrative.links.findIndex(
            (l) => l.id === parseInt(parentContentId, 10)
          );
          if (existingLink > -1) {
            byNarrative.links[existingLink].linked = false;
          }
        }
        return byNarrative;
      });

      setCurrentlyVisibleConnections(allConnections);

      setAssignedConnectedContent([kfaObject]);
    },
    [
      filteredAvailableConnectedContent,
      allConnections,
      parentContentId,
      setCurrentlyVisibleConnections,
    ]
  );

  const handleDeleteConnectedContent = useCallback(
    (contentToDelete) => {
      const editedNarrative = contentToDelete;
      if (editedNarrative.type === 200) {
        setLastSelectedKFA(null);
      }

      const existingLink = editedNarrative.links.findIndex(
        (l) => l.id === parseInt(useParentContentId, 10)
      );
      if (existingLink > -1) {
        editedNarrative.links[existingLink].linked = false;
      }

      const filteredContent = assignedConnectedContent.filter((e) => {
        return e !== contentToDelete;
      });

      linkNarrative(editedNarrative, connectedContentType);

      setAssignedConnectedContent(filteredContent);

      if (setConnectedAnalyses) {
        setConnectedAnalyses(filteredContent);
      }
    },
    [
      assignedConnectedContent,
      linkNarrative,
      connectedContentType,
      useParentContentId,
      setLastSelectedKFA,
      setConnectedAnalyses,
    ]
  );

  return (
    <>
      <br />
      <div
        className={classNames({
          'connected-content-controls': true,
          'last-item': lastItem,
        })}
      >
        <div>
          <ConditionalMultiSelectSearchWithLabel
            projectId={projectId}
            isLoading={isLoadingConnections}
            allNarratives={allConnections}
            error={error}
            options={filteredAvailableConnectedContent}
            onSubmit={(updatedSelectedTagTexts) => {
              if (connectedContentType === 'analyses') {
                submitAnalyses(updatedSelectedTagTexts);
              } else {
                submitKeyFocusArea(updatedSelectedTagTexts);
              }
            }}
            parentContentId={useParentContentId}
            linkNarrative={(editingNarrative, typeOfNarrative) =>
              linkNarrative(editingNarrative, typeOfNarrative)
            }
            selectedTags={assignedConnectedContent}
            innerIdWhitelist={['connected-content-filter-search']}
            search={search}
            setSearch={handleSetSearch}
            connectedContentType={connectedContentType}
            searchField={searchField}
            modalTitle="Are you sure?"
            modalBody='It is possible to connect only one KFA for each analysis. Clicking on "connect" will replace the existing connection.'
            modalCheckboxLabel={t('dontShowThisMessageAgain')}
            connectedContent={assignedConnectedContent}
            isCommentingAllowedOnThisItem={isCommentingAllowedOnThisItem}
            pageContainerName={pageContainerName}
          />

          {assignedConnectedContent && assignedConnectedContent.length ? (
            <SelectedConnectedContentItem
              fullContent={assignedConnectedContent}
              onDelete={(contentToDelete) =>
                handleDeleteConnectedContent(contentToDelete)
              }
              parentContentId={useParentContentId}
              hideTextVersion={featureFlags.isVersioningPhase1Enabled}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default EditConnectedContentNew;
